import { Bundle } from "../Bundle";
import { Variant } from "../stepHandles/resource/variant";
import { FrameStepHandle } from "../stepHandles/steps/frame";
import { assetService } from "../services/asset";
import {
    ColorDefinition,
    ColorOption,
    fetchAsString,
    generateSVGWithUnknownColors,
    IllustrationStepHandle,
    optionService,
    ShapeStepHandle,
    svgToDataUrl,
    TextStepHandle,
    VariantResource,
    WorkflowExperience,
} from "..";
import {
    AddonHandle,
    AspectType,
    Asset,
    GlobalPropertyConfigurationAspect,
    GlobalPropertyState,
    GlobalPropertyStateColorOptionStorage,
    GlobalPropertyStateFileUploadStorage,
    OptionResource,
    PatternImageData,
    StepType,
} from "../types";
import { GetSVGDimensions, patternImageDataCache } from "../util/frame";
import { modifySVGColors } from "../util/illustration";

/**
 * TODO: Optimize workflow experience state updates.
 */

export class GlobalPropertyHandleService {
    private readonly bundle: Bundle;
    private handleCache: { [key: string]: GlobalPropertyHandle } = {};

    constructor(bundle: Bundle) {
        this.bundle = bundle;
    }

    /**
     * Gets a handle for the given global property. Fetches from cache if available.
     * @param property The property to get a handle for.
     * @returns A promise resolving to the handle.
     */
    async getHandle(property: GlobalPropertyConfigurationAspect): Promise<GlobalPropertyHandle> {
        if (this.handleCache[property.name]) return this.handleCache[property.name];
        const handle = await this.createHandle(property);
        this.handleCache[property.name] = handle;
        return handle;
    }

    /**
     * Gets a list of handles for all global properties. Fetches from cache if available.
     * @returns A promise resolving to the handles.
     */
    async getHandles(): Promise<GlobalPropertyHandle[]> {
        if (!this.bundle.getProductCollection()?.getResource().globalPropertyConfiguration) return [];
        await this.bundle.getGlobalPropertyStateManager().getInitializationPromise();
        const configuration = this.bundle.getProductCollection()!.getResource().globalPropertyConfiguration!;
        return Promise.all(configuration.aspects.map((p) => this.getHandle(p)));
    }

    /**
     * Applies the global state to all handles, if the state is set.
     * @param handles The handles to apply the state to.
     * @param state
     * @returns
     */
    applyConditionsFromState(
        handles: GlobalPropertyHandle[],
        state: GlobalPropertyState | undefined,
    ): GlobalPropertyHandle[] {
        if (!state) return handles;
        return handles.filter((h) => {
            return this.aspectConditionsSatisfied(h.getRawProperty(), state);
        });
    }

    private aspectConditionsSatisfied(aspect: GlobalPropertyConfigurationAspect, state: GlobalPropertyState): boolean {
        if (!aspect.conditions || aspect.conditions.length === 0) return true;
        return aspect.conditions.some((c) => {
            const targetAspect = state.aspects.find((a) => a.name === c.targetAspectName);
            if (!targetAspect) return false;
            return c.requiredVariantSelections.some((v) => v === targetAspect.value);
        });
    }

    private async createHandle(property: GlobalPropertyConfigurationAspect): Promise<GlobalPropertyHandle> {
        switch (property.type) {
            case AspectType.FileUpload:
                return new FileUploadGlobalPropertyHandle(this.bundle, property).initialize();
            case AspectType.ColorOption:
                return new ColorOptionGlobalPropertyHandle(
                    this.bundle,
                    property,
                    property.entityId ? await optionService.getOption(property.entityId!) : undefined,
                );
            case AspectType.Option: {
                const option = property.entityId ? await optionService.getOption(property.entityId!) : undefined;
                if (option?.type === "Color") {
                    return new ColorOptionGlobalPropertyHandle(this.bundle, property, option);
                } else {
                    return new OptionGlobalPropertyHandle(this.bundle, property, option);
                }
            }
            case AspectType.Text:
                return new TextGlobalPropertyHandle(this.bundle, property);
            default:
                throw new Error("Unhandled Global Property Aspect Type");
        }
    }
}

/**
 * A GlobalPropertyHandle acts as an interface to global properties on our platform. Currently
 * bundles can return a handle for any global properties associated to the collection.
 */
export abstract class GlobalPropertyHandle {
    protected readonly bundle: Bundle;
    protected readonly property: GlobalPropertyConfigurationAspect;

    constructor(bundle: Bundle, property: GlobalPropertyConfigurationAspect) {
        this.bundle = bundle;
        this.property = property;
    }

    /**
     * @returns The name of the global property. This is the key used to store the property in the state.
     */
    getName() {
        return this.property.name;
    }

    /**
     * @returns A human friendly title.
     */
    getTitle() {
        return this.property.title;
    }

    /**
     * @returns A human friendly description.
     */
    getDescription() {
        return this.property.description;
    }

    /**
     * @returns The type of the global property. Use this to determine how to render the property.
     */
    getType(): AspectType {
        return this.property.type;
    }

    /**
     * @returns The underlying property data object.
     */
    getRawProperty(): GlobalPropertyConfigurationAspect {
        return this.property;
    }

    /**
     * Applies the global state to all shared steps, if the state is set.
     * @param targetExperiences Optionally filter the workflow experiences it should be applied to.
     */
    abstract applyGlobalState(targetExperiences?: WorkflowExperience[]): Promise<void>;

    /**
     * Returns all steps that share this property.
     * @param targetExperiences Optionally filter the steps to only those in the given experiences.
     */
    protected getSharedSteps(targetExperiences?: WorkflowExperience[]) {
        const experiences = targetExperiences ?? this.bundle.getWorkflowExperiences();
        const sharedSteps = experiences.flatMap((e) =>
            e.getStepsConditionallyActive().filter((s) => {
                if (s.getOverrideGlobalPropertyConfiguration(this.property.type)) {
                    return false;
                }
                return s
                    .getRaw()
                    .globalPropertyAspectConfigurations?.some(
                        (c) =>
                            c.globalPropertyConfigurationId ===
                                this.bundle.getProductCollection()?.getResource().globalPropertyConfiguration?.id &&
                            c.aspectName === this.property.name,
                    );
            }),
        );
        return sharedSteps;
    }

    protected getStateValue(): string | undefined {
        return this.bundle.getGlobalPropertyStateManager().getAspect(this.property.name);
    }
}

/**
 * A file upload global property allows for setting a frame image against all shared steps.
 */
export class FileUploadGlobalPropertyHandle extends GlobalPropertyHandle {
    private colorOption?: OptionResource;
    private imageData?: PatternImageData;

    constructor(bundle: Bundle, property: GlobalPropertyConfigurationAspect) {
        super(bundle, property);
    }

    async initialize(): Promise<FileUploadGlobalPropertyHandle> {
        if (this.property.data?.fileUpload?.colorOptionId) {
            this.colorOption = await optionService.getOption(this.property.data?.fileUpload?.colorOptionId);
        }
        await this.loadImageData();
        return this;
    }

    private async loadImageData() {
        const img = await this.getImage();
        if (img?.fileLink) {
            if (img?.fileLink.endsWith(".svg")) {
                const originalSvg = await fetchAsString(img?.fileLink);
                const svgData = await GetSVGDimensions(img?.fileLink);
                const width = svgData.width;
                const height = svgData.height;
                const svgAndColors = await generateSVGWithUnknownColors(originalSvg);
                const storage = this.bundle
                    .getGlobalPropertyStateManager()
                    .getAspectStorage<GlobalPropertyStateFileUploadStorage>(this.property.name);
                storage?.colors?.forEach((it) => {
                    svgAndColors.colors[it.key] = {
                        browserValue: it.browserValue,
                        pmsValue: it.pmsValue,
                    };
                });
                const data = {
                    src: img?.fileLink,
                    width,
                    height,
                    aspect: width / height,
                    svg: svgAndColors.svg,
                    colors: svgAndColors.colors,
                };
                patternImageDataCache.set(img?.fileLink, data);
                this.imageData = data;
            } else {
                this.imageData = undefined;
            }
        }
    }

    /**
     * Select an image to be used for all shared steps.
     * @param asset The asset to use.
     * @returns A promise resolving when all affected steps have been updated.
     */
    async selectImage(asset: Asset) {
        const stateManager = this.bundle.getGlobalPropertyStateManager();
        await Promise.all([
            stateManager.setAspect(this.property.name, asset.key!, {
                ...(stateManager.getAspectStorage(this.property.name) || {}),
                originalAssetKey: asset.key,
                backgroundRemovedAssetKey: undefined,
                useOriginalAsset: undefined,
            }),
            this.applyImageSelection(asset),
            this.loadImageData(),
        ]);
    }

    async canUseBackgroundRemover(): Promise<boolean> {
        return this.bundle.getClient().canUseAddon(AddonHandle.BackgroundRemover);
    }

    /**
     * Removes the background from an image, stores it in the state, and returns the new asset.
     * @param applyNewAsset Optionally applies the new asset to all shared steps. Default: `true`.
     * @returns A promise that resolves with the newly generated Asset.
     */
    async removeBackgroundFromImage(applyNewAsset = true): Promise<Asset> {
        if (!(await this.canUseBackgroundRemover())) {
            throw new Error(
                "The current Integration does not have access to the Background Remover. Please call canUseBackgroundRemover to ensure you disable this feature when appropriate.",
            );
        }
        const image = await this.getOriginalImage();
        if (!image) {
            throw new Error("You must supply an image selection before attempting to remove the background.");
        }
        const stateManager = this.bundle.getGlobalPropertyStateManager();
        const bgRemoved = await assetService.removeBackgroundFromAsset(image);
        const existingStorage = stateManager.getAspectStorage(this.property.name);
        const updatedStorage: GlobalPropertyStateFileUploadStorage = {
            ...existingStorage,
            backgroundRemovedAssetKey: bgRemoved.key,
            useOriginalAsset: !applyNewAsset,
        };
        const updatePromise = applyNewAsset
            ? stateManager.setAspect(this.property.name, bgRemoved.key!, updatedStorage)
            : stateManager.setAspectStorage(this.property.name, updatedStorage);
        const promises = [updatePromise];
        if (applyNewAsset) {
            promises.push(this.applyImageSelection(bgRemoved, undefined, false, false));
            promises.push(this.loadImageData());
        }
        await Promise.all(promises);
        this.updateSharedStepStorage(updatedStorage);
        return bgRemoved;
    }

    /**
     * Returns `true` if the state has an image assigned, otherwise `false`.
     */
    hasImage(): boolean {
        return !!this.getStateValue();
    }

    /**
     * Retrieves the current image selection, if one exists.
     * @returns A promise that resolves with an `Asset` object if one is assigned to the state, otherwise `undefined`.
     */
    async getImage(): Promise<Asset | undefined> {
        const stateValue = this.getStateValue();
        if (!stateValue) {
            return undefined;
        }
        return assetService.getLocalOrFromServer(stateValue);
    }

    async getImageWithColors(): Promise<string | undefined> {
        await this.loadImageData();
        if (!this.imageData?.svg) {
            return this.imageData?.src;
        }
        return svgToDataUrl(modifySVGColors(this.imageData.svg, this.imageData.colors ?? {}, false));
    }

    /**
     * Retrieves the original, unmodified image selection, if one exists.
     * @returns A promise that resolves with an `Asset` object if one is assigned to the state, otherwise `undefined`.
     */
    async getOriginalImage(): Promise<Asset | undefined> {
        const storage = this.bundle
            .getGlobalPropertyStateManager()
            .getAspectStorage<GlobalPropertyStateFileUploadStorage>(this.property.name);
        if (!storage?.originalAssetKey) {
            return undefined;
        }
        return assetService.getLocalOrFromServer(storage.originalAssetKey);
    }

    /**
     * Retrieves the version of the image selection that has the background removed, if one exists.
     * @returns A promise that resolves with an `Asset` object if one is assigned to the state, otherwise `undefined`.
     */
    async getBackgroundRemovedImage(): Promise<Asset | undefined> {
        const storage = this.bundle
            .getGlobalPropertyStateManager()
            .getAspectStorage<GlobalPropertyStateFileUploadStorage>(this.property.name);
        if (!storage?.backgroundRemovedAssetKey) {
            return undefined;
        }
        return assetService.getLocalOrFromServer(storage.backgroundRemovedAssetKey);
    }

    getUseOriginalImage(): boolean {
        return (
            this.bundle
                .getGlobalPropertyStateManager()
                .getAspectStorage<GlobalPropertyStateFileUploadStorage>(this.property.name)?.useOriginalAsset ?? false
        );
    }

    async setUseOriginalImage(value: boolean): Promise<void> {
        const originalImage = await this.getOriginalImage();
        if (!originalImage) {
            throw new Error("You must select an image before calling setUseOriginalImage");
        }
        const stateManager = this.bundle.getGlobalPropertyStateManager();
        const existingStorage = stateManager.getAspectStorage<GlobalPropertyStateFileUploadStorage>(this.property.name);
        if (existingStorage?.useOriginalAsset === value) {
            return;
        }
        const updatedStorage = {
            ...existingStorage,
            useOriginalAsset: value,
        };
        const targetKey = value ? existingStorage?.originalAssetKey : existingStorage?.backgroundRemovedAssetKey;
        const promises = [stateManager.setAspect(this.property.name, targetKey || "", updatedStorage)];
        if (value) {
            promises.push(this.applyImageSelection(originalImage, undefined, false, false));
        } else {
            const updateUsingBg = async () => {
                const bgRemovedImage = await this.getBackgroundRemovedImage();
                if (!bgRemovedImage) {
                    throw new Error(
                        "You must call removeBackgroundFromImage before attempting to use that version of the image.",
                    );
                }
                this.applyImageSelection(bgRemovedImage, undefined, false, false);
            };
            promises.push(updateUsingBg());
        }
        promises.push(this.loadImageData());
        await Promise.all(promises);
        this.updateSharedStepStorage(updatedStorage);
    }

    async applyGlobalState(targetExperiences?: WorkflowExperience[]) {
        const aspect = this.getStateValue();
        if (!aspect) return;
        const asset = await assetService.getLocalOrFromServer(aspect);
        if (!asset) return;
        await this.applyImageSelection(asset, targetExperiences, false, true);
        const storage = this.bundle
            .getGlobalPropertyStateManager()
            .getAspectStorage<GlobalPropertyStateFileUploadStorage>(this.property.name);
        if (storage) {
            this.updateSharedStepStorage({ ...storage });
            if (storage.colors) {
                const colorMap = new Map(
                    storage.colors!.map((it) => [it.key, { browserValue: it.browserValue, pmsValue: it.pmsValue }]) ??
                        [],
                );
                this.getSharedSteps(targetExperiences).forEach((step) => {
                    (step as FrameStepHandle).changeColors(colorMap);
                });
            }
        } else {
            this.updateSharedStepStorage({ originalAssetKey: aspect });
            await this.bundle
                .getGlobalPropertyStateManager()
                .setAspectStorage(this.property.name, { originalAssetKey: aspect });
        }
    }

    getAvailableColors(): ColorOption[] {
        if (!this.property.data?.fileUpload?.colorPickerEnabled) {
            return [];
        }
        return (
            this.colorOption?.variants?.map((variant: VariantResource) => {
                return {
                    fill: variant.color,
                    stroke: variant.color,
                    variant,
                    pmsValue: variant.name,
                };
            }) ?? []
        );
    }

    getCurrentColors():
        | {
              [key: string]: ColorDefinition;
          }
        | undefined {
        return this.imageData?.colors;
    }

    isColorPickerEnabled() {
        return this.property.data?.fileUpload?.colorPickerEnabled ?? false;
    }

    isPmsPickerEnabled() {
        return this.property.data?.fileUpload?.pmsPickerEnabled ?? false;
    }

    async getOriginalColors(): Promise<
        | {
              [key: string]: ColorDefinition;
          }
        | undefined
    > {
        if (!this.imageData?.svg) {
            return undefined;
        }
        const svgData = await generateSVGWithUnknownColors(this.imageData.svg);
        return svgData.colors;
    }

    async changeColors(newFills: { [key: string]: ColorDefinition }): Promise<void> {
        if (!this.imageData?.svg) {
            return;
        }
        const sharedSteps = this.getSharedSteps();
        const fills = new Map(Object.entries(newFills));
        sharedSteps.forEach((step) => {
            const frameStep = step as FrameStepHandle;
            frameStep.changeColors(fills);
        });
        const storage = this.bundle
            .getGlobalPropertyStateManager()
            .getAspectStorage<GlobalPropertyStateFileUploadStorage>(this.property.name);
        await Promise.all([
            this.bundle
                .getGlobalPropertyStateManager()
                .setAspectStorage<GlobalPropertyStateFileUploadStorage>(this.property.name, {
                    ...storage,
                    colors: Object.entries(newFills).map(([key, color]) => ({
                        key: key,
                        browserValue: color.browserValue,
                        pmsValue: color.pmsValue,
                    })),
                }),
            await this.loadImageData(),
        ]);
    }

    private async applyImageSelection(
        asset: Asset,
        targetExperiences?: WorkflowExperience[],
        useAsOriginalImage = true,
        updateFrameOffsets = true,
    ) {
        const sharedSteps = this.getSharedSteps(targetExperiences);
        const experiencesUpdates = sharedSteps.map((step) => {
            const frameStep = step as FrameStepHandle;
            if (!frameStep.getFrameService()) return;
            return frameStep.selectImage(asset, useAsOriginalImage, updateFrameOffsets);
        });
        await Promise.all(experiencesUpdates);
    }

    private updateSharedStepStorage(storage: GlobalPropertyStateFileUploadStorage) {
        const experiences = this.bundle.getWorkflowExperiences();
        experiences.forEach((e) =>
            e.getSteps().forEach((s) => {
                if (s.getOverrideGlobalPropertyConfiguration(this.property.type)) {
                    return;
                }
                const shouldUpdateStep = s
                    .getRaw()
                    .globalPropertyAspectConfigurations?.some(
                        (c) =>
                            c.globalPropertyConfigurationId ===
                                this.bundle.getProductCollection()?.getResource().globalPropertyConfiguration?.id &&
                            c.aspectName === this.property.name,
                    );
                if (shouldUpdateStep) {
                    e.getWorkflowManager().updateStorage(s.getId(), {
                        framePatternData: {
                            originalAssetKey: storage.originalAssetKey,
                            backgroundRemovedAssetKey: storage.backgroundRemovedAssetKey,
                            useOriginalAsset: storage.useOriginalAsset,
                        },
                    });
                }
            }),
        );
    }
}

export class TextGlobalPropertyHandle extends GlobalPropertyHandle {
    constructor(bundle: Bundle, property: GlobalPropertyConfigurationAspect) {
        super(bundle, property);
    }

    /**
     * Gets the current text
     */
    getText(): string {
        const aspectValue = this.getStateValue();
        if (!aspectValue) return "";
        return aspectValue;
    }

    /**
     * Set the text.
     */
    async setText(text: string) {
        await Promise.all([
            this.bundle.getGlobalPropertyStateManager().setAspect(this.property.name, text),
            this.applyTextSelection(text),
        ]);
    }

    async applyGlobalState(targetExperiences?: WorkflowExperience[]) {
        const value = this.getStateValue();
        if (!value) return Promise.resolve();
        await this.applyTextSelection(value, targetExperiences);
    }

    private async applyTextSelection(text: string, targetExperiences?: WorkflowExperience[]) {
        const sharedSteps = this.getSharedSteps(targetExperiences);
        const experiencesUpdates = sharedSteps.map((step) => {
            const textStep = step as TextStepHandle;
            textStep.setText(text);
        });
        await Promise.all(experiencesUpdates);
    }
}

/**
 * An option property handle represents a property that can
 * affect the option of steps across multiple transactions.
 */
export class OptionGlobalPropertyHandle extends GlobalPropertyHandle {
    protected optionResource?: OptionResource;

    constructor(
        bundle: Bundle,
        property: GlobalPropertyConfigurationAspect,
        optionResource: OptionResource | undefined,
    ) {
        super(bundle, property);
        this.optionResource = optionResource;
    }

    /**
     * If the option has a default variant, select it.
     */
    async initDefaultVariant() {
        const defaultVariant = this.optionResource?.variants?.find(
            (v) => v.id === this.optionResource?.defaultVariant?.id,
        );
        if (defaultVariant) {
            await this.selectVariant(new Variant(defaultVariant));
        }
    }

    /**
     * Gets the currently selected variant, or undefined if no variant is selected.
     */
    getCurrentVariant() {
        if (!this.optionResource) return undefined;
        const aspectValue = this.getStateValue();
        if (!aspectValue) {
            const defaultVariant = this.optionResource.variants?.find(
                (v) => v.id === this.optionResource?.defaultVariant?.id,
            );
            if (!defaultVariant) return undefined;
            return new Variant(defaultVariant);
        } else {
            const variantResource = this.optionResource.variants?.find((v) => v.id === aspectValue);
            if (!variantResource) return undefined;
            return new Variant(variantResource);
        }
    }

    /**
     * @returns A list of valid variants for this step. Does not include disabled variants.
     */
    getAvailableVariants() {
        const variants = this.optionResource?.variants?.filter((v) => v.enabled) || [];
        return variants.map((v) => new Variant(v));
    }

    /**
     * @returns A list of all variants for this step, including disabled ones.
     */
    getAllVariants() {
        const variants = this.optionResource?.variants || [];
        return variants.map((v) => new Variant(v));
    }

    /**
     * Select a given variant on the option for all shared steps.
     * @param variant The variant to select.
     */
    async selectVariant(variant: Variant, channel?: number) {
        console.log(`Selecting variant ${variant.getId()} for option global property handle (channel ${channel})`);
        await Promise.all([
            this.bundle
                .getGlobalPropertyStateManager()
                .setAspect(this.property.name, variant.getId(), undefined, channel),
            this.applyVariantSelection(variant),
        ]);
    }

    async applyGlobalState(targetExperiences?: WorkflowExperience[]) {
        const aspect = this.getStateValue();
        if (!aspect) return Promise.resolve();
        const variant = this.optionResource?.variants?.find((v) => v.id === aspect);
        if (!variant) return Promise.resolve();
        console.log(`Applying global state using variant ${variant.id} for aspect ${aspect}`);
        return this.applyVariantSelection(new Variant(variant), targetExperiences);
    }

    private async applyVariantSelection(variant: Variant, targetExperiences?: WorkflowExperience[]) {
        const sharedSteps = this.getSharedSteps(targetExperiences);
        const promises = sharedSteps.map((step) => {
            return step.selectVariant(variant);
        });
        await Promise.all(promises);
    }
}

/**
 * A color option property handle represents a property that can
 * affect the option of steps across multiple transactions. This class is a specialization of the OptionGlobalPropertyHandle which includes functionality for
 * setting custom color values when a custom color variant is selected.
 */
export class ColorOptionGlobalPropertyHandle extends OptionGlobalPropertyHandle {
    constructor(
        bundle: Bundle,
        property: GlobalPropertyConfigurationAspect,
        optionResource: OptionResource | undefined,
    ) {
        super(bundle, property, optionResource);
    }

    /**
     * Select a given variant on the option for all shared steps.
     * @param variant The variant to select.
     */
    override async selectVariant(variant: Variant, channel?: number) {
        console.log(
            `Selecting variant ${variant.getId()} for color option global property handle (channel ${channel})`,
        );
        await Promise.all([
            this.bundle
                .getGlobalPropertyStateManager()
                .setAspect(this.property.name, variant.getId(), undefined, channel),
            this.applyColorVariant(variant, undefined, channel),
        ]);
    }

    override async applyGlobalState(targetExperiences?: WorkflowExperience[]) {
        const aspect = this.getStateValue();
        if (!aspect) return Promise.resolve();
        const variant = this.optionResource?.variants?.find((v) => v.id === aspect);
        if (!variant) return Promise.resolve();
        await this.applyColorVariant(new Variant(variant), targetExperiences);
        const customColor = this.bundle
            .getGlobalPropertyStateManager()
            .getAspectStorage<GlobalPropertyStateColorOptionStorage>(this.property.name)?.customColor;
        if (customColor) this.setCustomColor(customColor);
    }

    private async applyColorVariant(variant: Variant, targetExperiences?: WorkflowExperience[], channel?: number) {
        const sharedSteps = this.getSharedSteps(targetExperiences);
        const promises = sharedSteps.map((step) => {
            switch (step.getType()) {
                case StepType.Shape:
                    return step.selectVariant(variant);
                case StepType.Text:
                    return (step as TextStepHandle).setFillColor({
                        fill: variant.getColor(),
                        stroke: variant.getColor(),
                        variant: variant.getResource(),
                    });
                case StepType.Illustration: {
                    const illustrationStep = step as IllustrationStepHandle;
                    return illustrationStep.setColor(channel?.toString() || "", variant.getColor() || "");
                }
            }
        });
        await Promise.all(promises);
    }

    /**
     * Sets a custom color on the global state.
     */
    setCustomColor(color: string) {
        const sharedSteps = this.getSharedSteps() as ShapeStepHandle[];
        sharedSteps.forEach((step) => step.setCustomColor(color));
        this.bundle
            .getGlobalPropertyStateManager()
            .setAspectStorage<GlobalPropertyStateColorOptionStorage>(this.property.name, { customColor: color });
    }

    /**
     * Gets the custom color used by the global state.
     */
    getCustomColor(): string {
        return (
            this.bundle
                .getGlobalPropertyStateManager()
                .getAspectStorage<GlobalPropertyStateColorOptionStorage>(this.property.name)?.customColor ||
            this.getCurrentVariant()?.getColor() ||
            "#ffffff"
        );
    }
}
